// Obligations / Cost-Recovery ledger hooks. Reads over the rows the money
// engine produces, plus the PR-2 guided waive (the ONE mutation this surface
// owns — it resolves BOTH representations, so it invalidates the obligation
// ledger AND the fee surfaces alongside loan health).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  obligationLedgerApi,
  type CostRecoveryLedgerParams,
  type ObligationExplainRequest,
  type WaiveObligationRequest,
} from "@/api/portfolioAdmin/obligationLedgerApi";
import { invalidateLoanHealth } from "@/hooks/portfolioAdmin/invalidation";
import { loanFeesKeys } from "@/hooks/portfolioAdmin/loanFeesKeys";
import { forwardProjectionKeys } from "@/hooks/portfolioAdmin/forwardProjectionKeys";
import { servicingKeys } from "@/hooks/portfolioAdmin/useServicing";
import { invalidateCycleScope } from "@/hooks/portfolioAdmin/useMonthlyCycle";
import { obligationLedgerKeys } from "./obligationLedgerKeys";

export function useLoanObligations(loanId: string | undefined) {
  return useQuery({
    queryKey: obligationLedgerKeys.loan(loanId ?? ""),
    queryFn: () => obligationLedgerApi.forLoan(loanId!),
    enabled: !!loanId,
  });
}

export function useCostRecoveryLedger(params?: CostRecoveryLedgerParams) {
  return useQuery({
    queryKey: obligationLedgerKeys.crossLoan(params),
    queryFn: () => obligationLedgerApi.crossLoan(params),
  });
}

// --- PR-6 Explain Engine ------------------------------------------------------

export function useObligationDetail(
  loanId: string | undefined,
  obligationId: string | undefined,
) {
  return useQuery({
    queryKey: obligationLedgerKeys.detail(loanId ?? "", obligationId ?? ""),
    queryFn: () => obligationLedgerApi.detail(loanId!, obligationId!),
    enabled: !!loanId && !!obligationId,
  });
}

export function useObligationComparison(loanId: string | undefined) {
  return useQuery({
    queryKey: obligationLedgerKeys.comparison(loanId ?? ""),
    queryFn: () => obligationLedgerApi.comparison(loanId!),
    enabled: !!loanId,
  });
}

/** Ask-AI: "explain this obligation" — a POST, but purely a read. */
export function useExplainObligation(loanId: string, obligationId: string) {
  return useMutation({
    // no-invalidation: read-only AI explanation; no cached reads change
    mutationFn: (body: ObligationExplainRequest) =>
      obligationLedgerApi.explain(loanId, obligationId, body),
  });
}

/** Guided waive — writes off the remaining balance (recovered amounts stand)
 *  and resolves the linked fee/statement representation server-side, so the
 *  fee list, review queue, and both obligation ledgers all refresh. */
export function useWaiveObligation(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { obligationId: string; body: WaiveObligationRequest }) =>
      obligationLedgerApi.waive(loanId, vars.obligationId, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: obligationLedgerKeys.loan(loanId) });
      void qc.invalidateQueries({ queryKey: obligationLedgerKeys.crossLoanPrefix() });
      void qc.invalidateQueries({ queryKey: loanFeesKeys.loan(loanId) });
      void qc.invalidateQueries({ queryKey: loanFeesKeys.crossLoanPrefix() });
      void qc.invalidateQueries({ queryKey: loanFeesKeys.reviewQueuePrefix() });
      void invalidateLoanHealth(qc, loanId);
      // A waived obligation drops out of the standing worksheet projection
      // immediately (its state is no longer pending/partially_paid).
      void qc.invalidateQueries({ queryKey: forwardProjectionKeys.projectionPrefix(loanId) });
      void qc.invalidateQueries({ queryKey: forwardProjectionKeys.incomePoolsForwardPrefix(loanId) });
      // ObligationWaiveService zeroes OutstandingBalance, which is exactly what
      // the ledger's `openBorrowerFeeObligations` sums — so the Record Payment
      // wizard's prefill anchor (PR #1099) must refresh too, or it keeps asking
      // the borrower for fee money that was just written off. Same coupling as
      // the fee-approve path in useLoanFees.
      void qc.invalidateQueries({ queryKey: servicingKeys.ledger(loanId) });
      // ObligationWaiveService REGENERATES every statement that billed this
      // obligation (ObligationWaiveService.cs:182 -> RegenerateAsync), which
      // mints a fresh row and supersedes the old one. The waive modal tells
      // the user that outright, so leaving the cockpit and the cross-loan
      // statement queues on the superseded bill contradicts the copy.
      invalidateCycleScope(qc, loanId);
    },
  });
}
